import React from 'react';

import { BounceCard } from '../BounceCard';
import { GlowAngleProperty } from '../BounceCard/BounceCard.styles';
import { ConfirmationCard } from '../ConfirmationCard';
import type { CardStatus, TimelineEntry } from '../types';
import { Strip, Slot } from './ScrollStrip.styles';

/** One card in the strip: a timeline entry plus its live-resolved visual status. */
export type StripCard = {
  entry: TimelineEntry;
  status: CardStatus;
};

export type ScrollStripProps = {
  /** The full deploy timeline as cards (confirm card only present once done). */
  cards: StripCard[];
  reduced: boolean;
  /** Active brand name for the confirmation headline ("{brand} is live"). */
  brandName?: string;
  /** Active brand domain for the confirmation card's Visit CTA. */
  domain?: string;
  /** Opens the deployment summary modal (confirmation card info CTA). */
  onOpenSummary: () => void;
};

/**
 * The horizontal scroll strip of deploy-step cards. It renders the WHOLE timeline in order
 * (completed on the left, the in-progress step in the middle, pending to the right) as a native
 * `overflow-x` row. Two hooks (owned by the parent, via the forwarded container ref) drive it:
 * {@link useStripScroll} auto-follows the live step and lets the user drag/wheel/chevron through
 * it, while {@link useStripChoreography} paints the premium scroll-linked motion — the card
 * nearest the centre snap is framed by a gap, straightened, and lifted, all continuous in the
 * scroll. The Slot itself carries only sizing + overlap; its transform is written imperatively.
 */
export const ScrollStrip = React.forwardRef<HTMLDivElement, ScrollStripProps>(
  ({ cards, reduced, brandName, domain, onOpenSummary }, ref) => {
    return (
      <Strip ref={ref}>
        <GlowAngleProperty />
        {cards.map((card) => (
          <Slot key={card.entry.id}>
            {card.status === 'confirm' ? (
              <ConfirmationCard brandName={brandName} domain={domain} onOpenSummary={onOpenSummary} reduced={reduced} />
            ) : (
              <BounceCard
                status={card.status}
                label={card.entry.label}
                stepNumber={card.entry.stepNumber}
                reduced={reduced}
              />
            )}
          </Slot>
        ))}
      </Strip>
    );
  },
);

ScrollStrip.displayName = 'ScrollStrip';

export default ScrollStrip;
